import { useCallback, useRef, useState, type ChangeEvent, type DragEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth.tsx';
import { createSubmission, markSubmissionSubmitted, uploadSubmissionPdf } from '../lib/db.ts';
import { supabase } from '../lib/supabase.ts';

const MAX_BYTES = 100 * 1024 * 1024;

type Stage =
  | { kind: 'idle' }
  | { kind: 'creating' }
  | { kind: 'uploading'; submissionId: string; progress: number }
  | { kind: 'finalizing'; submissionId: string }
  | { kind: 'error'; message: string };

export function JobNew() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const inputRef = useRef<HTMLInputElement>(null);
  const [jobName, setJobName] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [stage, setStage] = useState<Stage>({ kind: 'idle' });
  const [dragOver, setDragOver] = useState(false);

  const onPickFile = useCallback((picked: File | null): void => {
    if (!picked) return;
    if (picked.type !== 'application/pdf' && !picked.name.toLowerCase().endsWith('.pdf')) {
      setStage({ kind: 'error', message: 'Only PDF files are accepted' });
      return;
    }
    if (picked.size > MAX_BYTES) {
      setStage({ kind: 'error', message: `File is ${(picked.size / 1024 / 1024).toFixed(1)} MB; max is 100 MB` });
      return;
    }
    setFile(picked);
    if (!jobName) {
      setJobName(picked.name.replace(/\.pdf$/i, '').replace(/[_-]+/g, ' '));
    }
    setStage({ kind: 'idle' });
  }, [jobName]);

  function onDrop(e: DragEvent<HTMLDivElement>): void {
    e.preventDefault();
    setDragOver(false);
    const picked = e.dataTransfer.files[0] ?? null;
    onPickFile(picked);
  }

  function onFileInput(e: ChangeEvent<HTMLInputElement>): void {
    const picked = e.target.files?.[0] ?? null;
    onPickFile(picked);
  }

  async function onSubmit(): Promise<void> {
    if (!file || !user) return;
    if (!jobName.trim()) {
      setStage({ kind: 'error', message: 'Job name required' });
      return;
    }

    setStage({ kind: 'creating' });
    try {
      const submission = await createSubmission({
        jobName: jobName.trim(),
        sourcePdfFilename: file.name,
        userId: user.id,
      });
      setStage({ kind: 'uploading', submissionId: submission.id, progress: 0 });

      // Supabase Storage doesn't yet expose progress hooks via the JS SDK,
      // so we just await the upload and show "uploading" until it resolves.
      await uploadSubmissionPdf(submission.id, file);
      setStage({ kind: 'finalizing', submissionId: submission.id });

      await markSubmissionSubmitted(submission.id);

      navigate(`/app/jobs/${submission.id}`);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Upload failed';
      setStage({ kind: 'error', message });
    }
  }

  // Sign-in safety net — should never trigger because RequireAuth wraps this route.
  if (!user) {
    void supabase.auth.getSession();
    return null;
  }

  const busy = stage.kind === 'creating' || stage.kind === 'uploading' || stage.kind === 'finalizing';

  return (
    <div style={{ maxWidth: 640 }}>
      <h1 style={{ fontFamily: "'Syne', sans-serif", fontSize: 28, fontWeight: 800, marginBottom: 6 }}>
        New job
      </h1>
      <p style={{ color: '#B8BDD6', fontSize: 14, marginBottom: 24, lineHeight: 1.6 }}>
        Drop a drawing set as PDF (≤ 100 MB). The parser-worker picks it up automatically once submitted.
      </p>

      <label
        htmlFor="job-name"
        style={{ display: 'block', fontSize: 11, color: '#525870', fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.12em', marginBottom: 6, textTransform: 'uppercase' }}
      >
        Job name
      </label>
      <input
        id="job-name"
        type="text"
        value={jobName}
        onChange={(e) => setJobName(e.target.value)}
        disabled={busy}
        placeholder="e.g. Berry Creek II"
        style={{
          width: '100%',
          background: '#0D0F1C',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 6,
          padding: '11px 14px',
          color: '#F0F2FF',
          fontFamily: "'Outfit', sans-serif",
          fontSize: 14,
          marginBottom: 22,
          outline: 'none',
        }}
      />

      <div
        onDrop={onDrop}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onClick={() => !busy && inputRef.current?.click()}
        style={{
          border: `2px dashed ${dragOver ? '#00E87A' : 'rgba(255,255,255,0.14)'}`,
          background: dragOver ? 'rgba(0,232,122,0.04)' : '#0D0F1C',
          borderRadius: 12,
          padding: '40px 24px',
          textAlign: 'center',
          cursor: busy ? 'not-allowed' : 'pointer',
          transition: 'border-color 120ms ease, background 120ms ease',
          marginBottom: 18,
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf,.pdf"
          onChange={onFileInput}
          disabled={busy}
          style={{ display: 'none' }}
        />
        {file ? (
          <div>
            <div style={{ fontFamily: "'Syne', sans-serif", fontSize: 17, fontWeight: 700, marginBottom: 6 }}>
              {file.name}
            </div>
            <div style={{ color: '#525870', fontSize: 12 }}>{(file.size / 1024 / 1024).toFixed(2)} MB</div>
          </div>
        ) : (
          <div>
            <div style={{ fontFamily: "'Syne', sans-serif", fontSize: 16, fontWeight: 700, marginBottom: 6 }}>
              Drop PDF here, or click to browse
            </div>
            <div style={{ color: '#525870', fontSize: 12 }}>One file per job</div>
          </div>
        )}
      </div>

      {stage.kind === 'error' && (
        <div style={{ color: '#FF6B7A', fontSize: 13, marginBottom: 14 }}>{stage.message}</div>
      )}
      {stage.kind === 'creating' && (
        <div style={{ color: '#B8BDD6', fontSize: 13, marginBottom: 14 }}>Creating submission…</div>
      )}
      {stage.kind === 'uploading' && (
        <div style={{ color: '#B8BDD6', fontSize: 13, marginBottom: 14 }}>Uploading PDF…</div>
      )}
      {stage.kind === 'finalizing' && (
        <div style={{ color: '#B8BDD6', fontSize: 13, marginBottom: 14 }}>Finalizing…</div>
      )}

      <button
        type="button"
        onClick={() => void onSubmit()}
        disabled={!file || busy}
        style={{
          background: file && !busy ? '#00E87A' : 'rgba(0,232,122,0.25)',
          color: '#030804',
          fontFamily: "'Syne', sans-serif",
          fontWeight: 700,
          fontSize: 13,
          padding: '13px 26px',
          borderRadius: 8,
          border: 'none',
          cursor: file && !busy ? 'pointer' : 'not-allowed',
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
        }}
      >
        {busy ? 'Submitting…' : 'Submit to pipeline'}
      </button>
    </div>
  );
}
